import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
  }
}

interface GoogleAnalyticsProps {
  measurementId?: string;
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ 
  measurementId = 'G-XXXXXXXXXX' 
}) => {
  const location = useLocation();
  const observedSections = useRef(new Set<string>());

  useEffect(() => {
    // Track page views when route changes
    if (typeof window.gtag !== 'undefined') {
      console.log('🔍 GA: Tracking page view', {
        measurementId,
        page_path: location.pathname + location.search,
        page_title: document.title
      });
      window.gtag('config', measurementId, {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    } else {
      console.warn('⚠️ GA: gtag not available for page tracking');
    }
  }, [location, measurementId]);

  useEffect(() => {
    // Setup Intersection Observer for section tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const sectionId = entry.target.id;
            
            // Only track each section once per page load
            if (sectionId && !observedSections.current.has(sectionId)) {
              observedSections.current.add(sectionId);
              trackSectionView(sectionId);
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the section is visible
        rootMargin: '-10% 0px -10% 0px' // Add some margin to avoid false triggers
      }
    );

    // Observe all sections with IDs
    const sections = document.querySelectorAll('section[id], div[id]');
    sections.forEach((section) => {
      if (section.id && ['inicio', 'sobre', 'servicos', 'contato'].includes(section.id)) {
        observer.observe(section);
      }
    });

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
};

// Utility functions for tracking events
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window.gtag !== 'undefined') {
    console.log('📊 GA: Tracking event', {
      action,
      category,
      label,
      value
    });
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  } else {
    console.warn('⚠️ GA: gtag not available for event tracking', {
      action,
      category,
      label,
      value
    });
  }
};

// Specific tracking functions for the website
export const trackContactFormSubmit = () => {
  trackEvent('submit', 'contact_form', 'formulario_contato');
};

// Enhanced form tracking functions
export const trackFormSubmitAttempt = (formData: { name: string; phone: string; message?: string }) => {
  console.log('📝 GA: Form submit attempt', formData);
  trackEvent('form_submit_attempt', 'contact_form', 'tentativa_envio', 1);
  
  // Track form data (without sensitive info)
  if (typeof window.gtag !== 'undefined') {
    const eventData = {
      event_category: 'contact_form',
      has_name: !!formData.name,
      has_phone: !!formData.phone,
      has_message: !!formData.message,
      phone_length: formData.phone.replace(/\D/g, '').length
    };
    console.log('📊 GA: Form attempt details', eventData);
    window.gtag('event', 'form_attempt_details', eventData);
  }
};

export const trackFormSubmitSuccess = (formData: { name: string; phone: string; message?: string }) => {
  console.log('✅ GA: Form submit success', formData);
  trackEvent('form_submit_success', 'contact_form', 'envio_sucesso', 1);
  
  // Track successful submission details
  if (typeof window.gtag !== 'undefined') {
    const eventData = {
      event_category: 'contact_form',
      user_name: formData.name,
      user_phone: formData.phone,
      has_message: !!formData.message,
      message_length: formData.message?.length || 0,
      submission_time: new Date().toISOString()
    };
    console.log('📊 GA: Form success details', eventData);
    window.gtag('event', 'form_success_details', eventData);
  }
};

export const trackFormSubmitError = (error: any, formData?: { name: string; phone: string; message?: string }) => {
  console.log('❌ GA: Form submit error', { error, formData });
  trackEvent('form_submit_error', 'contact_form', 'erro_envio', 1);
  
  // Track error details
  if (typeof window.gtag !== 'undefined') {
    const eventData = {
      event_category: 'contact_form',
      error_message: error?.message || 'Erro desconhecido',
      error_code: error?.code || 'unknown',
      user_name: formData?.name || 'não informado',
      user_phone: formData?.phone || 'não informado',
      error_time: new Date().toISOString()
    };
    console.log('📊 GA: Form error details', eventData);
    window.gtag('event', 'form_error_details', eventData);
  }
};

export const trackPhoneClick = () => {
  trackEvent('click', 'contact', 'telefone');
};

export const trackEmailClick = () => {
  trackEvent('click', 'contact', 'email');
};

export const trackServiceClick = (serviceName: string) => {
  trackEvent('click', 'service', serviceName);
};

export const trackNavigationClick = (section: string) => {
  trackEvent('click', 'navigation', section);
};

// Track section views
export const trackSectionView = (sectionId: string) => {
  console.log('👁️ GA: Section viewed', { sectionId });
  trackEvent('view', 'section', sectionId);
};

export default GoogleAnalytics;